import {
  Alert,
  AlertIcon,
  FormControl,
  FormLabel,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AsyncSelect, Select } from 'chakra-react-select';
import { useRef, useState } from 'react';

import { exclusionApi, ExclusionQuery, ExclusionRule } from '../api';

export interface FieldOption {
  value: string;
  label: string;
}
export interface ExclusionFieldsValue {
  product: FieldOption | null;
  channel: FieldOption | null;
  plant: FieldOption | null;
  cg1: FieldOption | null;
}
export const EMPTY_FIELDS: ExclusionFieldsValue = {
  product: null,
  channel: null,
  plant: null,
  cg1: null,
};
export function fieldsFromQuery(query: ExclusionQuery): ExclusionFieldsValue {
  return {
    product: query.product_item_code
      ? {
          value: String(query.product_item_code),
          label: String(query.product_item_code),
        }
      : null,
    channel: query.distribution_channel
      ? { value: query.distribution_channel, label: query.distribution_channel }
      : null,
    plant: query.plant
      ? { value: String(query.plant), label: String(query.plant) }
      : null,
    cg1: query.cg_1_business_segmentation
      ? {
          value: query.cg_1_business_segmentation,
          label: query.cg_1_business_segmentation,
        }
      : null,
  };
}
export function queryFromFields(value: ExclusionFieldsValue): ExclusionQuery {
  return {
    product_item_code: value.product ? Number(value.product.value) : undefined,
    distribution_channel: value.channel?.value,
    plant: value.plant ? Number(value.plant.value) : undefined,
    cg_1_business_segmentation: value.cg1?.value,
  };
}
export function ruleFromFields(
  value: ExclusionFieldsValue
): ExclusionRule | null {
  if (!value.product || !value.channel || !value.plant) return null;
  return {
    product_item_code: Number(value.product.value),
    distribution_channel: value.channel.value,
    plant: Number(value.plant.value),
    cg_1_business_segmentation: value.cg1?.value ?? null,
  };
}

export function ExclusionFields({
  value,
  onChange,
  prefix,
  required = false,
  disabled = false,
}: {
  value: ExclusionFieldsValue;
  onChange: (value: ExclusionFieldsValue) => void;
  prefix: string;
  required?: boolean;
  disabled?: boolean;
}) {
  const { access_token, userId } = useAuthStore();
  const api = exclusionApi(access_token);
  const client = useQueryClient();
  const searchGeneration = useRef(0);
  const [productError, setProductError] = useState(false);
  let plantPlaceholder = 'All branches';
  if (required) plantPlaceholder = 'Choose a branch';
  if (required && !value.channel) plantPlaceholder = 'Choose a channel first';
  const channels = useQuery({
    queryKey: ['exclude-branch-lookup', userId, 'channels'],
    queryFn: api.channels,
    staleTime: 60000,
    retry: false,
  });
  const cg1 = useQuery({
    queryKey: ['exclude-branch-lookup', userId, 'cg1'],
    queryFn: api.cg1,
    staleTime: 60000,
    retry: false,
  });
  const plants = useQuery({
    queryKey: [
      'exclude-branch-lookup',
      userId,
      'plants',
      value.channel?.value ?? '',
    ],
    queryFn: () => api.plants(value.channel?.value ?? ''),
    enabled: !required || !!value.channel,
    staleTime: 60000,
    retry: false,
  });
  const loadProducts = async (search: string): Promise<FieldOption[]> => {
    const generation = ++searchGeneration.current;
    await new Promise((resolve) => setTimeout(resolve, 250));
    if (searchGeneration.current !== generation) return [];
    try {
      const result = await client.fetchQuery({
        queryKey: ['exclude-branch-lookup', userId, 'products', search],
        queryFn: () => api.products(search),
        staleTime: 60000,
      });
      if (searchGeneration.current !== generation) return [];
      setProductError(false);
      return result.data.map((product) => ({
        value: String(product.item_code),
        label: `${product.item_code} · ${product.title}`,
      }));
    } catch {
      if (searchGeneration.current === generation) setProductError(true);
      return [];
    }
  };
  return (
    <>
      {(channels.isError || cg1.isError || plants.isError || productError) && (
        <Alert status="error" borderRadius="md" mb={3}>
          <AlertIcon />
          Some lookup options could not load. Reopen or search again to retry.
        </Alert>
      )}
      <SimpleGrid columns={{ base: 1, md: required ? 1 : 4 }} spacing={4}>
        <FormControl isRequired={required}>
          <FormLabel htmlFor={`${prefix}-product`} fontSize="sm">
            Product
          </FormLabel>
          <AsyncSelect<FieldOption, false>
            inputId={`${prefix}-product`}
            value={value.product}
            onChange={(product) => onChange({ ...value, product })}
            loadOptions={loadProducts}
            defaultOptions
            isClearable={!required}
            isDisabled={disabled}
            placeholder="Name or item code"
            noOptionsMessage={() =>
              productError ? 'Unable to load products' : 'No products found'
            }
          />
        </FormControl>
        <FormControl isRequired={required}>
          <FormLabel htmlFor={`${prefix}-channel`} fontSize="sm">
            Distribution channel
          </FormLabel>
          <Select<FieldOption, false>
            inputId={`${prefix}-channel`}
            value={value.channel}
            onChange={(channel) => onChange({ ...value, channel, plant: null })}
            options={
              channels.data?.data.map((row) => ({
                value: row.distribution_channel,
                label: `${row.distribution_channel} · ${row.distribution_channel_desc}`,
              })) ?? []
            }
            isClearable={!required}
            isLoading={channels.isPending}
            isDisabled={disabled || channels.isError}
            placeholder={required ? 'Choose a channel' : 'All channels'}
          />
        </FormControl>
        <FormControl isRequired={required}>
          <FormLabel htmlFor={`${prefix}-plant`} fontSize="sm">
            Branch / plant
          </FormLabel>
          <Select<FieldOption, false>
            inputId={`${prefix}-plant`}
            value={value.plant}
            onChange={(plant) => onChange({ ...value, plant })}
            options={
              plants.data?.data
                .filter(
                  (row) =>
                    /^\d+$/.test(row.plant_id) && Number(row.plant_id) > 0
                )
                .map((row) => ({
                  value: row.plant_id,
                  label: `${row.plant_id} · ${
                    row.plant_name ?? 'Name unavailable'
                  }`,
                })) ?? []
            }
            isClearable={!required}
            isLoading={plants.isFetching}
            isDisabled={
              disabled || plants.isError || (required && !value.channel)
            }
            placeholder={plantPlaceholder}
          />
        </FormControl>
        <FormControl>
          <FormLabel htmlFor={`${prefix}-cg1`} fontSize="sm">
            CG1 scope
          </FormLabel>
          <Select<FieldOption, false>
            inputId={`${prefix}-cg1`}
            value={value.cg1}
            onChange={(cg1) => onChange({ ...value, cg1 })}
            options={
              cg1.data?.data.map((row) => ({
                value: row.cg_1_business_segmentation,
                label: `${row.cg_1_business_segmentation} · ${row.cg_1_business_segmentation_desc}`,
              })) ?? []
            }
            isClearable
            isDisabled={disabled || cg1.isError}
            isLoading={cg1.isPending}
            placeholder={
              required ? 'All ship-tos (any CG1)' : 'All rule scopes'
            }
          />
          {required && (
            <Text mt={2} fontSize="sm" color="gray.600">
              Leave empty to exclude the product regardless of ship-to CG1.
            </Text>
          )}
        </FormControl>
      </SimpleGrid>
    </>
  );
}
